/**
 * ITD Calculator - Formula-based calculator for pricing
 *
 * Used by: itd_calculator.blade.php
 */

interface FormulaVars {
    [key: string]: number;
}

const ALLOWED_KEYS = ['Backspace', 'Delete', 'Tab', 'Home', 'End', 'ArrowLeft', 'ArrowRight'];

/**
 * Safely evaluates a formula string.
 * Replaces {var} with vars[var] (input values)
 * Replaces [var] with vars[var] (data values / intermediate results)
 */
function resolveFormula(formula: string | undefined, vars: FormulaVars): number {
    if (!formula || typeof formula !== 'string') {
        return 0;
    }

    let processedFormula = formula;

    // Replace {varName} with input values from the vars object
    processedFormula = processedFormula.replace(/\{([a-zA-Z0-9_]+)\}/g, (_match, varName: string) => {
        return String(parseFloat(String(vars[varName])) || 0);
    });

    // Replace [dataName] with intermediate results or data attributes
    processedFormula = processedFormula.replace(/\[([a-zA-Z0-9_-]+)\]/g, (_match, dataName: string) => {
        return String(parseFloat(String(vars[dataName])) || 0);
    });

    try {
        // Construct a safe execution context for the math string
        const result = new Function(`return Number(${processedFormula})`)() as number;

        return isFinite(result) ? result : 0;
    } catch (e) {
        console.error('Calculator: Formula evaluation failed:', processedFormula, e);
        
        return 0;
    }
}

function capitalizeFirst(str: string): string {
    if (!str) {
        return '';
    }
    
    return str.charAt(0).toUpperCase() + str.slice(1);
}



/**
 * Aggregates all input values and calculates total costs and savings.
 */
function calculateTotals(calculator: HTMLElement): void {
    const hardwareInput = calculator.querySelector<HTMLInputElement>('input[name="hardware"]:checked');
    const hardwareMode = hardwareInput ? hardwareInput.value : 'rent';
    const modeKey = capitalizeFirst(hardwareMode);
    
    let totalCost = 0;
    let totalSavings = 0;

    calculator.querySelectorAll<HTMLInputElement>('input[type="text"][name]').forEach((input) => {
        const totalFormula = input.dataset[`formula${modeKey}Total`];
        const savingsFormula = input.dataset[`formula${modeKey}Savings`];
        const inputValue = parseFloat(input.value) || 0;

        if (inputValue === 0) {
            return;
        }

        const formulaVars: FormulaVars = {};
        const inputName = input.getAttribute('name');

        if (inputName) {
            formulaVars[inputName] = inputValue;
        }

        // Calculate line item total
        const lineTotal = resolveFormula(totalFormula, formulaVars);
        formulaVars[`data-formula-${hardwareMode}-total`] = lineTotal;

        totalCost = totalCost + lineTotal;
        totalSavings = totalSavings + resolveFormula(savingsFormula, formulaVars);
    });

    // Add tachogram costs for rent mode
    if (hardwareMode === 'rent' && totalCost > 0 && totalSavings > 0) {
        const monthlyInput = calculator.querySelector<HTMLInputElement>('input[name="benefits"][value="monthly"]');

        if (monthlyInput) {
            const tachogramCost = parseFloat(monthlyInput.dataset.tachogramTotal || '0') || 0;
            const tachogramSavings = parseFloat(monthlyInput.dataset.tachogramSavings || '0') || 0;

            totalCost += tachogramCost;
            totalSavings += tachogramSavings;
        }
    }

    const totalElement = calculator.querySelector('[data-result-total]');
    const savingsElement = calculator.querySelector('[data-result-savings]');

    if (totalElement) {
        totalElement.textContent = String(Math.round(totalCost));
    }

    if (savingsElement) {
        savingsElement.textContent = String(Math.round(totalSavings));
    }
}

function initCalculator(calculator: HTMLElement): void {
    // 1. Radio Linking (e.g., Selecting "Buy" hardware selects "Lifetime" benefits)
    calculator.querySelectorAll<HTMLInputElement>('input[type="radio"][data-link]').forEach((radio) => {
        radio.addEventListener('change', function(this: HTMLInputElement) {
            if (this.checked === false) {
                return;
            }

            const linkData = this.dataset.link;
            
            if (linkData) {
                const [targetName, targetValue] = linkData.split(':');
                const targetInput = calculator.querySelector<HTMLInputElement>(
                    `input[name="${targetName}"][value="${targetValue}"]`
                );
    
                if (targetInput) {
                    targetInput.checked = true;
                }
            }
        });
    });

    // All radio buttons trigger recalculation
    calculator.querySelectorAll<HTMLInputElement>('input[type="radio"]').forEach((radio) => {
        radio.addEventListener('change', () => {
            calculateTotals(calculator);
        });
    });

    // Text inputs with numeric validation
    calculator.querySelectorAll<HTMLInputElement>('input[type="text"][name]').forEach((input) => {
        input.dataset.lastValue = input.value || '';

        input.addEventListener('keydown', (e: KeyboardEvent) => {
            if (ALLOWED_KEYS.includes(e.key) === true) {
                return;
            }

            if (e.shiftKey === true || /^\d$/.test(e.key) === false) {
                e.preventDefault();
            }
        });

        const handleInputChange = (): void => {
            if (input.value !== input.dataset.lastValue) {
                calculateTotals(calculator);
                input.dataset.lastValue = input.value;
            }
        };

        input.addEventListener('change', handleInputChange);
        input.addEventListener('keyup', handleInputChange);
    });
}

// Auto-initialize all calculators on the page
document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll<HTMLElement>('.mp-component-itd-calculator').forEach((calculator) => {
        initCalculator(calculator);
    });
});

export { initCalculator, calculateTotals, resolveFormula };